import { useCallback, useState } from 'react';

interface ProvisionResult {
  status: 'idle' | 'loading' | 'success' | 'error';
  error?: string;
}

/**
 * Extracts fields from the provision token's payload (base64url-encoded JSON).
 * The token format is: <base64url-payload>.<hmac-signature>
 */
function decodeProvisionPayload(token: string): {
  backend_url: string;
  auth_token: string;
  gateway_token?: string;
} | null {
  try {
    const [data] = token.split('.');
    if (!data) return null;
    const payload = JSON.parse(atob(data.replace(/-/g, '+').replace(/_/g, '/')));
    if (!payload.backend_url || !payload.auth_token) return null;
    return {
      backend_url: payload.backend_url,
      auth_token: payload.auth_token,
      gateway_token: payload.gateway_token,
    };
  } catch {
    return null;
  }
}

export function useProvisionToken() {
  const [result, setResult] = useState<ProvisionResult>({ status: 'idle' });

  const consumeToken = useCallback(async (token: string) => {
    setResult({ status: 'loading' });

    const payload = decodeProvisionPayload(token);
    if (!payload) {
      setResult({ status: 'error', error: 'Invalid provision token' });
      return false;
    }

    localStorage.setItem('clawer_api_url', payload.backend_url);
    localStorage.setItem('clawer_auth_token', payload.auth_token);
    if (payload.gateway_token) {
      localStorage.setItem('clawer_gateway_token', payload.gateway_token);
      // Persist to VPS backend so it survives localStorage clears (fire-and-forget)
      fetch(`${payload.backend_url}/api/setup/gateway-token`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${payload.auth_token}`,
        },
        body: JSON.stringify({ gateway_token: payload.gateway_token }),
      }).catch(() => {
        // Non-blocking — localStorage is the primary store
      });
    }
    setResult({ status: 'success' });
    return true;
  }, []);

  return { ...result, consumeToken };
}
